import React from "react";
import { useNode, useEditor, Element } from "@craftjs/core";
import { 
  FormControl, 
  FormLabel, 
  TextField, 
  Select, 
  MenuItem, 
  Checkbox, 
  FormControlLabel,
  Typography,
  Chip,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";

export const BulmaElement = ({
  element = 'div',
  bulmaClass = '',
  modifiers = [],
  content = '',
  width = 'auto',
  height = 'auto',
  hidden = false,
  canvas = false,
  style = {},
  children,
  ...props
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  if (hidden) return null;

  // Build the complete className
  const className = [bulmaClass, ...modifiers].filter(Boolean).join(' ');
  
  // Create the element dynamically
  const HtmlElement = element;

  // Selection styling
  const getSelectionStyle = () => {
    if (!enabled) return {};
    
    if (isSelected) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };

  const elementStyle = {
    width: width === 'auto' ? undefined : width,
    height: height === 'auto' ? undefined : height,
    minHeight: canvas ? '50px' : undefined, // Ensure drop zone visibility
    transition: 'all 0.2s ease',
    ...style,
    ...getSelectionStyle()
  };

  return (
    <HtmlElement 
      ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
      className={className}
      style={elementStyle}
      {...props}
    >
      {canvas ? children : (content || children)}
    </HtmlElement>
  );
};

const BulmaElementSettings = () => {
  const { 
    actions: { setProp }, 
    element,
    bulmaClass,
    modifiers,
    content,
    width,
    height,
    canvas
  } = useNode((node) => ({
    element: node.data.props.element,
    bulmaClass: node.data.props.bulmaClass,
    modifiers: node.data.props.modifiers,
    content: node.data.props.content,
    width: node.data.props.width,
    height: node.data.props.height,
    canvas: node.data.props.canvas
  }));

  // HTML Elements
  const htmlElements = [
    'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'button', 'a', 'section', 'article', 'aside', 'header', 
    'footer', 'nav', 'main', 'figure', 'figcaption'
  ];

  // Bulma Base Classes organized by category
  const bulmaClasses = {
    Layout: [
      'container', 'section', 'hero', 'hero-body', 'hero-head', 'hero-foot',
      'level', 'level-left', 'level-right', 'level-item',
      'media', 'media-left', 'media-content', 'media-right',
      'columns', 'column'
    ],
    Elements: [
      'box', 'button', 'content', 'delete', 'icon', 'image',
      'notification', 'progress', 'table', 'tag', 'tags', 'title', 'subtitle'
    ],
    Components: [
      'breadcrumb', 'card', 'card-header', 'card-content', 'card-footer',
      'dropdown', 'dropdown-trigger', 'dropdown-menu', 'dropdown-content',
      'menu', 'menu-list', 'menu-label',
      'message', 'message-header', 'message-body',
      'modal', 'modal-background', 'modal-content', 'modal-card',
      'navbar', 'navbar-brand', 'navbar-menu', 'navbar-item',
      'pagination', 'panel', 'tabs'
    ],
    Form: [
      'field', 'control', 'label', 'input', 'textarea', 'select',
      'checkbox', 'radio', 'file', 'help'
    ]
  };

  // Bulma Modifiers organized by type
  const bulmaModifiers = {
    Colors: [
      'is-primary', 'is-link', 'is-info', 'is-success', 'is-warning', 'is-danger',
      'is-black', 'is-dark', 'is-light', 'is-white'
    ],
    Sizes: [
      'is-small', 'is-normal', 'is-medium', 'is-large',
      'is-size-1', 'is-size-2', 'is-size-3', 'is-size-4', 'is-size-5', 'is-size-6', 'is-size-7'
    ],
    States: [
      'is-active', 'is-disabled', 'is-loading', 'is-selected',
      'is-hovered', 'is-focused', 'is-outlined', 'is-inverted'
    ],
    Responsive: [
      'is-mobile', 'is-tablet', 'is-desktop', 'is-widescreen', 'is-fullhd',
      'is-hidden-mobile', 'is-hidden-tablet', 'is-hidden-desktop'
    ],
    Typography: [
      'has-text-centered', 'has-text-justified', 'has-text-left', 'has-text-right',
      'has-text-weight-light', 'has-text-weight-normal', 'has-text-weight-medium',
      'has-text-weight-semibold', 'has-text-weight-bold',
      'is-capitalized', 'is-lowercase', 'is-uppercase', 'is-italic'
    ],
    Display: [
      'is-block', 'is-flex', 'is-inline', 'is-inline-block', 'is-inline-flex',
      'is-hidden', 'is-invisible', 'is-clipped'
    ],
    Flexbox: [
      'is-justify-content-center', 'is-justify-content-space-between',
      'is-justify-content-flex-start', 'is-justify-content-flex-end',
      'is-align-items-center', 'is-align-items-flex-start', 'is-align-items-flex-end',
      'is-flex-direction-column', 'is-flex-direction-row'
    ]
  };

  const toggleModifier = (modifier) => {
    const currentModifiers = modifiers || [];
    const newModifiers = currentModifiers.includes(modifier)
      ? currentModifiers.filter(m => m !== modifier)
      : [...currentModifiers, modifier];
    setProp(props => props.modifiers = newModifiers);
  };

  const isModifierActive = (modifier) => {
    return (modifiers || []).includes(modifier);
  };

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>HTML Element</FormLabel>
        <Select
          value={element || 'div'}
          onChange={(e) => setProp(props => props.element = e.target.value)}
          size="small"
        >
          {htmlElements.map(el => (
            <MenuItem key={el} value={el}>{el}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Content</FormLabel>
        <TextField
          value={content || ''}
          onChange={(e) => setProp(props => props.content = e.target.value)}
          size="small"
          placeholder="Text content"
          multiline
          rows={2}
          disabled={canvas}
          helperText={canvas ? "Content disabled when acting as container" : ""}
        />
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={canvas}
            onChange={(e) => setProp(props => props.canvas = e.target.checked)}
          />
        }
        label="Act as container (can hold other elements)"
      />

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Bulma Base Classes
      </Typography>
      
      {Object.entries(bulmaClasses).map(([category, classes]) => (
        <Accordion key={category}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body2">{category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {classes.map(cls => (
                <Grid item key={cls}>
                  <Chip
                    label={cls}
                    variant={bulmaClass === cls ? "filled" : "outlined"}
                    color={bulmaClass === cls ? "primary" : "default"}
                    size="small"
                    clickable
                    onClick={() => setProp(props => props.bulmaClass = cls)}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Bulma Modifiers
      </Typography>

      {Object.entries(bulmaModifiers).map(([category, mods]) => (
        <Accordion key={category}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body2">{category}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              {mods.map(modifier => (
                <Grid item key={modifier}>
                  <Chip
                    label={modifier}
                    variant={isModifierActive(modifier) ? "filled" : "outlined"}
                    color={isModifierActive(modifier) ? "secondary" : "default"}
                    size="small"
                    clickable
                    onClick={() => toggleModifier(modifier)}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
        Active Classes
      </Typography>
      <Box sx={{ mb: 2 }}>
        {bulmaClass && (
          <Chip 
            label={bulmaClass} 
            color="primary" 
            size="small" 
            sx={{ mr: 1, mb: 1 }}
            onDelete={() => setProp(props => props.bulmaClass = '')}
          />
        )}
        {(modifiers || []).map(modifier => (
          <Chip 
            key={modifier}
            label={modifier} 
            color="secondary" 
            size="small" 
            sx={{ mr: 1, mb: 1 }}
            onDelete={() => toggleModifier(modifier)}
          />
        ))}
      </Box>

      <FormControl fullWidth margin="normal">
        <FormLabel>Width</FormLabel>
        <TextField
          value={width || 'auto'}
          onChange={(e) => setProp(props => props.width = e.target.value)}
          size="small"
          placeholder="auto, 100%, 300px"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Height</FormLabel>
        <TextField
          value={height || 'auto'}
          onChange={(e) => setProp(props => props.height = e.target.value)}
          size="small"
          placeholder="auto, 100px, 50vh"
        />
      </FormControl>
    </div>
  );
};

BulmaElement.craft = {
  props: {
    element: 'div',
    bulmaClass: '',
    modifiers: [],
    content: '',
    width: 'auto',
    height: 'auto',
    hidden: false,
    canvas: false
  },
  related: {
    settings: BulmaElementSettings
  },
  rules: {
    canDrag: () => true,
    canDrop: (node, target) => {
      return target.data.props.canvas;
    },
    canMoveIn: (incomingNodes, currentNode, helper) => {
      return currentNode.data.props.canvas;
    }
  }
};